import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/Card';
import { Button } from '../components/Button';
import { Badge } from '../components/Badge';
import { Bell, CheckCircle2, FileText, ShieldAlert, AlertTriangle, Target, ServerCrash, Check, Trash2 } from 'lucide-react';
import { notificationsList } from '../data/mockData';
import { EmptyState } from '../components/EmptyState';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { FilterBar, FilterField } from '../components/FilterBar';
import { Filter } from 'lucide-react';
export default function Notifications() {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState(notificationsList);
  const [showFilters, setShowFilters] = useState(false);
  const [filterValues, setFilterValues] = useState<Record<string, any>>({});
  const handleFilterChange = (key: string, value: any) => {
    setFilterValues(prev => ({
      ...prev,
      [key]: value
    }));
  };
  const handleResetFilters = () => {
    setFilterValues({});
  };
  const unreadCount = notifications.filter(n => !n.isRead).length;
  const filteredNotifications = notifications.filter(n => {
    const search = filterValues.search?.toLowerCase() || '';
    const matchesSearch = !search || n.title.toLowerCase().includes(search) || n.message.toLowerCase().includes(search);
    const matchesType = !filterValues.type || filterValues.type === 'all' || n.type === filterValues.type;
    const matchesStatus = !filterValues.status || filterValues.status === 'all' || (filterValues.status === 'Belum Dibaca' ? !n.isRead : n.isRead);
    return matchesSearch && matchesType && matchesStatus;
  });
  const filterFields: FilterField[] = [{
    key: 'search',
    label: 'Pencarian',
    type: 'search',
    placeholder: 'Cari judul, pesan...'
  }, {
    key: 'type',
    label: 'Jenis Notifikasi',
    type: 'select',
    options: [{
      label: 'Laporan baru',
      value: 'Laporan baru'
    }, {
      label: 'Butuh verifikasi',
      value: 'Butuh verifikasi'
    }, {
      label: 'Peringatan kritis',
      value: 'Peringatan kritis'
    }, {
      label: 'Pembaruan KPI',
      value: 'Pembaruan KPI'
    }, {
      label: 'Peringatan sistem',
      value: 'Peringatan sistem'
    }]
  }, {
    key: 'status',
    label: 'Status',
    type: 'select',
    options: [{
      label: 'Belum Dibaca',
      value: 'Belum Dibaca'
    }, {
      label: 'Sudah Dibaca',
      value: 'Sudah Dibaca'
    }]
  }, {
    key: 'date',
    label: 'Tanggal',
    type: 'datePreset'
  }];
  const handleMarkAsRead = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setNotifications(notifications.map(n => n.id === id ? {
      ...n,
      isRead: true
    } : n));
  };
  const handleMarkAllAsRead = () => {
    setNotifications(notifications.map(n => ({
      ...n,
      isRead: true
    })));
    toast.success('Semua notifikasi ditandai sudah dibaca.');
  };
  const handleClearAll = () => {
    setNotifications([]);
    toast.success('Semua notifikasi telah dihapus.');
  };
  const getIconForType = (type: string) => {
    switch (type) {
      case 'Laporan baru':
        return <FileText className="w-4 h-4 text-primary" data-id="element-2563" />;
      case 'Butuh verifikasi':
        return <ShieldAlert className="w-4 h-4 text-[var(--status-standby)]" data-id="element-2564" />;
      case 'Peringatan kritis':
        return <AlertTriangle className="w-4 h-4 text-destructive" data-id="element-2565" />;
      case 'Pembaruan KPI':
        return <Target className="w-4 h-4 text-[var(--status-active)]" data-id="element-2566" />;
      case 'Peringatan sistem':
        return <ServerCrash className="w-4 h-4 text-muted-foreground" data-id="element-2567" />;
      default:
        return <Bell className="w-4 h-4 text-primary" data-id="element-2568" />;
    }
  };
  return <div className="space-y-6 animate-in fade-in duration-500 max-w-5xl mx-auto pb-12" data-id="element-2569">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end gap-4 border-b border-border/50 pb-4" data-id="element-2570">
        <div data-id="element-2571">
          <h1 className="text-2xl font-heading font-bold tracking-tight text-foreground flex items-center gap-3" data-id="element-2572">
            <Bell className="w-6 h-6 text-primary text-glow-primary" data-id="element-2573" />
            PUSAT NOTIFIKASI
            {unreadCount > 0 && <Badge variant="destructive" className="ml-2 font-mono text-xs" data-id="element-2574">
                {unreadCount} BARU
              </Badge>}
          </h1>
          <p className="text-muted-foreground font-mono text-xs mt-1 uppercase tracking-widest" data-id="element-2575">
            Pembaruan Sistem & Peringatan Taktis
          </p>
        </div>
        <div className="flex gap-2" data-id="element-2576">
          <Button variant="outline" size="sm" onClick={handleMarkAllAsRead} disabled={unreadCount === 0} className="font-mono text-xs uppercase tracking-widest" data-id="element-2577">
            <Check className="w-3 h-3 mr-2" data-id="element-2578" /> Tandai Semua Dibaca
          </Button>
          <Button variant="outline" size="sm" onClick={handleClearAll} disabled={notifications.length === 0} className="font-mono text-xs uppercase tracking-widest text-destructive hover:text-destructive hover:bg-destructive/10 border-destructive/50" data-id="element-2579">
            <Trash2 className="w-3 h-3 mr-2" data-id="element-2580" /> Bersihkan
          </Button>
        </div>
      </div>

      <Card className="glass-panel-heavy border-border/50 shadow-[0_0_20px_rgba(0,0,0,0.5)]" data-id="element-2581">
        <CardHeader className="p-4 border-b border-border/50 bg-card/50 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-2582">
          <div className="flex gap-2" data-id="element-2583">
            <Button variant={showFilters ? 'default' : 'outline'} size="sm" onClick={() => setShowFilters(!showFilters)} className={`h-8 border-border/50 font-mono text-xs ${showFilters ? 'bg-primary text-primary-foreground' : 'bg-background/50'}`} data-id="element-2584">
              <Filter className="w-4 h-4 mr-2" data-id="element-2585" />
              Filter
            </Button>
          </div>
          <div className="text-xs font-mono text-muted-foreground uppercase tracking-widest" data-id="element-2586">
            Menampilkan {filteredNotifications.length} Notifikasi
          </div>
        </CardHeader>
        <CardContent className="p-0" data-id="element-2587">
          {showFilters && <div className="p-4 border-b border-border/50 bg-black/20" data-id="element-2588">
              <FilterBar fields={filterFields} values={filterValues} onChange={handleFilterChange} onApply={() => {}} onReset={handleResetFilters} resultCount={filteredNotifications.length} data-id="element-2589" />
            </div>}
          {filteredNotifications.length === 0 ? <EmptyState icon={CheckCircle2} title="TIDAK ADA NOTIFIKASI" description="Tidak ada notifikasi yang sesuai dengan kriteria filter saat ini." actionLabel="Bersihkan Filter" onAction={handleResetFilters} className="border-0 rounded-none m-4" data-id="element-2590" /> : <div className="divide-y divide-border/30" data-id="element-2591">
              {filteredNotifications.map(notif => <div key={notif.id} onClick={() => {
            if (!notif.isRead) {
              setNotifications(notifications.map(n => n.id === notif.id ? {
                ...n,
                isRead: true
              } : n));
            }
            if (notif.link) navigate(notif.link);
          }} className={`p-4 flex gap-4 transition-colors cursor-pointer group ${notif.isRead ? 'bg-transparent hover:bg-white/5 opacity-70' : 'bg-primary/5 hover:bg-primary/10'}`} data-id="element-2592">
                  <div className="shrink-0 mt-1" data-id="element-2593">
                    <div className={`w-8 h-8 rounded flex items-center justify-center border ${notif.isRead ? 'bg-black/40 border-border/50' : 'bg-background border-primary/30 shadow-[0_0_10px_rgba(var(--primary),0.2)]'}`} data-id="element-2594">
                      {getIconForType(notif.type)}
                    </div>
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-2595">
                    <div className="flex justify-between items-start gap-4 mb-1" data-id="element-2596">
                      <h4 className={`text-sm font-bold truncate ${notif.isRead ? 'text-foreground/80' : 'text-foreground'}`} data-id="element-2597">
                        {notif.title}
                      </h4>
                      <span className="text-[10px] font-mono text-muted-foreground whitespace-nowrap shrink-0" data-id="element-2598">
                        {notif.time}
                      </span>
                    </div>
                    <p className={`text-xs font-mono leading-relaxed mb-2 ${notif.isRead ? 'text-muted-foreground' : 'text-foreground/90'}`} data-id="element-2599">
                      {notif.message}
                    </p>
                    <div className="flex items-center gap-3" data-id="element-2600">
                      <Badge variant="outline" className={`text-[9px] font-mono uppercase tracking-widest ${notif.type === 'Peringatan kritis' ? 'border-destructive/50 text-destructive' : notif.type === 'Butuh verifikasi' ? 'border-[var(--status-standby)]/50 text-[var(--status-standby)]' : notif.type === 'Pembaruan KPI' ? 'border-[var(--status-active)]/50 text-[var(--status-active)]' : notif.type === 'Laporan baru' ? 'border-primary/50 text-primary' : 'border-border/50 text-muted-foreground'}`} data-id="element-2601">
                        {notif.type}
                      </Badge>
                      {!notif.isRead && <button onClick={e => handleMarkAsRead(notif.id, e)} className="text-[10px] font-mono text-primary hover:text-primary/80 uppercase tracking-widest opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-2602">
                          Tandai Dibaca
                        </button>}
                    </div>
                  </div>
                  {!notif.isRead && <div className="shrink-0 flex items-center" data-id="element-2603">
                      <div className="w-2 h-2 rounded-full bg-primary shadow-[0_0_5px_var(--primary)] animate-pulse" data-id="element-2604"></div>
                    </div>}
                </div>)}
            </div>}
        </CardContent>
      </Card>
    </div>;
}